import { action, computed, makeObservable, observable } from 'mobx';
import { MulticheckSummaryState, SelectionId } from './Types';

interface Selections<T> {
  selected: Map<SelectionId, T>;
  deselected: Map<SelectionId, T>;
}

const selections = <T>(): Selections<T> => ({
  selected: new Map(),
  deselected: new Map(),
});

export class MulticheckStore<T> {
  @observable
  private selections: Selections<T> = selections();

  constructor() {
    makeObservable(this);
  }

  @action
  selectAll = (): void => {
    this.selections.deselected.forEach((value, key) => this.selections.selected.set(key, value));
    this.selections.deselected.clear();
  };

  @action
  deselectAll = (): void => {
    this.selections.selected.forEach((value, key) => this.selections.deselected.set(key, value));
    this.selections.selected.clear();
  };

  @action
  select = (id: SelectionId): void => {
    const selection = this.selections.deselected.get(id);
    if (selection) {
      this.selections.selected.set(id, selection);
      this.selections.deselected.delete(id);
    }
  };

  @action
  deselect = (id: SelectionId): void => {
    const selection = this.selections.selected.get(id);
    if (selection) {
      this.selections.deselected.set(id, selection);
      this.selections.selected.delete(id);
    }
  };

  @action
  addOption = (option: T, id: SelectionId): void => {
    this.removeOption(id);
    this.selections.deselected.set(id, option);
  };

  @action
  removeOption = (id: SelectionId): void => {
    this.selections.selected.delete(id);
    this.selections.deselected.delete(id);
  };

  isSelected = (id: SelectionId): boolean => {
    return this.selections.selected.has(id);
  };

  @computed
  get summaryState(): MulticheckSummaryState {
    if (this.selections.selected.size === 0) return 'none-checked';
    if (this.selections.deselected.size === 0) return 'all-checked';
    return 'some-checked';
  }

  @computed
  get selected(): ReadonlyArray<T> {
    return [...this.selections.selected.values()];
  }
}
